import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calculator,
  Ruler,
  Move,
  ArrowUpDown,
  Thermometer,
  Sun,
  PanelTop,
  Users,
  Tv,
  Snowflake,
  Download,
  Send,
  Loader2,
  User,
  Smartphone,
  CheckCircle2,
} from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";
import { generateTonnagePDF } from "@/lib/tonnagePdf";

// Standard AC sizes (in Tons) available in the market
const STANDARD_SIZES = [
  0.8, 1.0, 1.5, 1.6, 2.0, 2.2, 2.5, 3.0, 4.0, 5.5, 8.5, 11, 16.5, 22,
];

// Top brands dealt on the website (subset of AC Sale section)
const SUGGESTED_BRANDS = [
  "Mitsubishi Heavy Duty",
  "Mitsubishi Electric",
  "O General",
  "Daikin",
  "Blue Star",
  "Hitachi",
  "Toshiba",
  "TCL",
];

const ROOF_LABELS = {
  no: "No Exposure",
  indoor_top: "Indoor at Top Floor",
  outdoor_sun: "Outdoor placed in Direct Sun",
  both: "Both (Top Floor + Direct Sun)",
};
const ROOF_PCT = { no: 0, indoor_top: 0.25, outdoor_sun: 0.3, both: 0.5 };
const WINDOW_LABELS = { standard: "Standard", large: "Large West-facing Glass" };
const EQUIP_LABELS = {
  none: "None",
  standard: "Standard TV & Laptop",
  high: "High Heat Equipment",
  ind1: "Industrial Heating Equipment Grade 1",
  ind2: "Industrial Heating Equipment Grade 2",
  ind3: "Industrial Heating Equipment Grade 3",
  ind4: "Industrial Heating Equipment Grade 4",
  ind5: "Industrial Heating Equipment Grade 5",
  ind6: "Industrial Heating Equipment Grade 6",
};
const EQUIP_BTU = {
  none: 0,
  standard: 0,
  high: 1000,
  ind1: 12000,
  ind2: 18000,
  ind3: 24000,
  ind4: 36000,
  ind5: 48000,
  ind6: 60000,
};

// Determine recommended standard size + system type
const getRecommendation = (tonnage) => {
  let standard = STANDARD_SIZES.find((s) => s >= tonnage);
  let systemType = "Split AC";
  if (tonnage > 22) {
    standard = null;
    systemType = "Multi-VRF / Ductable System";
  } else if (tonnage > 8.5) {
    systemType = "VRF / Ductable System";
  } else if (tonnage > 4) {
    systemType = "Ductable AC";
  } else if (tonnage > 2) {
    systemType = "Cassette / Ductable AC";
  } else {
    systemType = "Split AC";
  }
  return { standard, systemType };
};

const SliderInput = ({ icon: Icon, label, value, setValue, min, max, step, unit, testid }) => (
  <div className="space-y-3" data-testid={`tc-input-${testid}`}>
    <div className="flex items-center justify-between">
      <Label className="flex items-center gap-2 text-gray-700 font-medium text-sm">
        <Icon className="h-4 w-4 text-amber-500" />
        {label}
      </Label>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            setValue(isNaN(v) ? 0 : v);
          }}
          className="w-20 h-9 bg-gray-50 border-gray-300 text-amber-600 font-bold text-center focus-visible:ring-amber-500"
          data-testid={`tc-number-${testid}`}
        />
        <span className="text-gray-500 text-xs w-8">{unit}</span>
      </div>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => setValue(v[0])}
      className="[&_[role=slider]]:bg-amber-500 [&_[role=slider]]:border-amber-400 [&>span:first-child]:bg-gray-200 [&_.bg-primary]:bg-amber-500"
      data-testid={`tc-slider-${testid}`}
    />
  </div>
);

const TonnageCalculatorSection = () => {
  // Primary inputs
  const [length, setLength] = useState(12);
  const [breadth, setBreadth] = useState(12);
  const [height, setHeight] = useState(10);
  const [temp, setTemp] = useState(40);

  // Secondary inputs
  const [roof, setRoof] = useState("no"); // no | direct | topfloor
  const [windowExp, setWindowExp] = useState("standard"); // standard | large
  const [occupants, setOccupants] = useState(2);
  const [equipment, setEquipment] = useState("none"); // none | standard | high

  const result = useMemo(() => {
    const volume = length * breadth * height;
    const baseBTU = volume * 10;
    const fTemp = temp > 35 ? 1 + (temp - 35) * 0.015 : 1.0;
    const subtotal = baseBTU * fTemp;

    // Secondary adjustments
    const roofExtra = subtotal * (ROOF_PCT[roof] || 0);
    const windowExtra = windowExp === "large" ? 1000 : 0;
    const occupantExtra = Math.max(0, occupants - 2) * 400;
    const equipmentExtra = EQUIP_BTU[equipment] || 0;

    const totalBTU = subtotal + roofExtra + windowExtra + occupantExtra + equipmentExtra;
    const tonnage = totalBTU / 12000;
    const { standard, systemType } = getRecommendation(tonnage);

    return {
      volume,
      baseBTU,
      fTemp,
      subtotal,
      roofExtra,
      windowExtra,
      occupantExtra,
      equipmentExtra,
      totalBTU,
      tonnage,
      standard,
      systemType,
    };
  }, [length, breadth, height, temp, roof, windowExp, occupants, equipment]);

  const fmt = (n) => Math.round(n).toLocaleString("en-IN");
  const recSizeLabel = result.standard
    ? `${result.standard} Ton ${result.systemType}`
    : result.systemType;

  // ===== Lead capture (Download + WhatsApp) =====
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sent, setSent] = useState(false);
  const [pdfOk, setPdfOk] = useState(true);
  const [refId, setRefId] = useState("");

  const genRefId = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const rand = Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
    return `MAC-${stamp}-${rand}`;
  };

  const handleGenerate = async () => {
    setError("");
    const cleanMobile = mobile.replace(/\D/g, "").slice(-10);
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (cleanMobile.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setGenerating(true);
    const now = new Date();
    const ref = genRefId();
    setRefId(ref);
    const dateTime = now.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const data = {
      name: name.trim(),
      mobile: cleanMobile,
      refId: ref,
      date: now.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }),
      dateTime,
      length,
      breadth,
      height,
      temp,
      roofLabel: ROOF_LABELS[roof],
      windowLabel: WINDOW_LABELS[windowExp],
      occupants,
      equipmentLabel: EQUIP_LABELS[equipment],
      brands: SUGGESTED_BRANDS,
      recSizeLabel,
      ...result,
    };

    try {
      await generateTonnagePDF(data);
      setPdfOk(true);
    } catch (e) {
      // PDF failed but still proceed to WhatsApp so the lead is captured
      setPdfOk(false);
      console.error("PDF generation failed", e);
    }

    // Build WhatsApp enquiry to the business number (9825884460)
    const msg =
      `*New AC Tonnage Enquiry (from website Tonnage Calculator)*\n\n` +
      `Ref ID: ${ref}\n` +
      `Date/Time: ${dateTime}\n\n` +
      `Name: ${data.name}\n` +
      `Mobile: +91 ${cleanMobile}\n\n` +
      `Room: ${length} x ${breadth} x ${height} ft (${fmt(result.volume)} cu.ft)\n` +
      `Peak Temp: ${temp} C\n` +
      `Roof: ${data.roofLabel} | Window: ${data.windowLabel}\n` +
      `Occupants: ${occupants} | Heat Sources: ${data.equipmentLabel}\n\n` +
      `*Recommended: ${recSizeLabel}*\n` +
      `Capacity: ${result.tonnage.toFixed(2)} tons / ${fmt(result.totalBTU)} BTU/hr\n\n` +
      `I've downloaded my PDF report. Please share the best price & availability.`;

    setTimeout(() => {
      window.open(getWhatsAppLink(msg), "_blank", "noopener,noreferrer");
      setGenerating(false);
      setSent(true);
    }, 400);
  };

  return (
    <section
      id="tonnage-calculator"
      className="py-20 bg-gradient-to-br from-white via-amber-50/40 to-white relative overflow-hidden"
      data-testid="tonnage-calculator-section"
    >
      {/* Decorative glow */}
      <div className="absolute top-1/4 -left-20 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 -right-20 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 section-animate">
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-4">
            Free Tool
          </Badge>
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3"
            data-testid="tonnage-title"
          >
            <Calculator className="h-8 w-8 text-amber-500" />
            Tonnage Calculator
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find the exact AC cooling capacity your space needs. Adjust the values below
            for an instant, accurate tonnage recommendation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* ===== INPUTS CARD ===== */}
          <Card className="bg-white border border-gray-200 shadow-xl">
            <CardContent className="p-6 md:p-8 space-y-8">
              {/* Primary */}
              <div>
                <h3 className="text-amber-600 font-bold uppercase tracking-wider text-xs mb-5">
                  Room Dimensions
                </h3>
                <div className="space-y-6">
                  <SliderInput icon={Ruler} label="Length" value={length} setValue={setLength} min={4} max={100} step={0.5} unit="ft" testid="length" />
                  <SliderInput icon={Move} label="Breadth" value={breadth} setValue={setBreadth} min={4} max={100} step={0.5} unit="ft" testid="breadth" />
                  <SliderInput icon={ArrowUpDown} label="Height" value={height} setValue={setHeight} min={7} max={30} step={0.5} unit="ft" testid="height" />
                  <SliderInput icon={Thermometer} label="Highest Summer Temp" value={temp} setValue={setTemp} min={30} max={52} step={1} unit="°C" testid="temp" />
                </div>
              </div>

              {/* Secondary */}
              <div>
                <h3 className="text-amber-600 font-bold uppercase tracking-wider text-xs mb-5">
                  Heat Load Factors
                </h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Roof */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                      <Sun className="h-4 w-4 text-amber-500" /> Roof Exposure
                    </Label>
                    <Select value={roof} onValueChange={setRoof}>
                      <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-700 focus:ring-amber-500" data-testid="tc-select-roof">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No Exposure</SelectItem>
                        <SelectItem value="indoor_top">Indoor at Top Floor (+25%)</SelectItem>
                        <SelectItem value="outdoor_sun">Outdoor placed in Direct Sun (+30%)</SelectItem>
                        <SelectItem value="both">Both Case Together (+50%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Window */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                      <PanelTop className="h-4 w-4 text-amber-500" /> Window Exposure
                    </Label>
                    <Select value={windowExp} onValueChange={setWindowExp}>
                      <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-700 focus:ring-amber-500" data-testid="tc-select-window">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="large">Large West-facing Glass (+1000 BTU)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Occupancy */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                      <Users className="h-4 w-4 text-amber-500" /> Occupants
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={occupants}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setOccupants(isNaN(v) ? 1 : v);
                      }}
                      className="bg-gray-50 border-gray-300 text-amber-600 font-bold focus-visible:ring-amber-500"
                      data-testid="tc-input-occupants"
                    />
                  </div>

                  {/* Equipment */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                      <Tv className="h-4 w-4 text-amber-500" /> Heat Sources
                    </Label>
                    <Select value={equipment} onValueChange={setEquipment}>
                      <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-700 focus:ring-amber-500" data-testid="tc-select-equipment">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="standard">Standard TV & Laptop</SelectItem>
                        <SelectItem value="high">High Heat Equipment (+1,000 BTU)</SelectItem>
                        <SelectItem value="ind1">Industrial Heating Equipment Grade 1 (+12,000 BTU)</SelectItem>
                        <SelectItem value="ind2">Industrial Heating Equipment Grade 2 (+18,000 BTU)</SelectItem>
                        <SelectItem value="ind3">Industrial Heating Equipment Grade 3 (+24,000 BTU)</SelectItem>
                        <SelectItem value="ind4">Industrial Heating Equipment Grade 4 (+36,000 BTU)</SelectItem>
                        <SelectItem value="ind5">Industrial Heating Equipment Grade 5 (+48,000 BTU)</SelectItem>
                        <SelectItem value="ind6">Industrial Heating Equipment Grade 6 (+60,000 BTU)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== RESULTS CARD ===== */}
          <div className="space-y-6 lg:sticky lg:top-24">
            {/* Recommended box */}
            <div
              className="rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-500 p-[2px] shadow-xl shadow-amber-500/25"
              data-testid="tonnage-result-box"
            >
              <div className="relative rounded-2xl bg-gradient-to-br from-amber-50 via-white to-yellow-50 p-6 md:p-8 text-center overflow-hidden">
                {/* soft decorative glow */}
                <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 bg-amber-300/30 rounded-full blur-3xl" />
                <div className="pointer-events-none absolute -bottom-12 -left-8 w-40 h-40 bg-yellow-300/20 rounded-full blur-3xl" />

                <div className="relative flex items-center justify-center gap-2 text-amber-700 mb-3">
                  <Snowflake className="h-5 w-5 animate-spin" style={{ animationDuration: "6s" }} />
                  <span className="uppercase tracking-widest text-xs font-bold">
                    Recommended Cooling Capacity
                  </span>
                </div>
                <div
                  className="relative text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 drop-shadow-sm transition-all duration-300"
                  data-testid="tonnage-value"
                  key={result.tonnage.toFixed(2)}
                >
                  {result.tonnage.toFixed(2)}{" "}
                  <span className="text-3xl text-gray-800">Tons</span>
                </div>
                <p className="relative text-gray-600 mt-2 text-sm font-medium" data-testid="tonnage-btu">
                  {fmt(result.totalBTU)} BTU/hr
                </p>
                <div className="relative mt-5 inline-block bg-white/80 backdrop-blur-sm border border-amber-300 rounded-xl px-5 py-3 shadow-sm">
                  <p className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-1">Suggested Standard Unit</p>
                  <p className="text-lg font-bold text-gray-900" data-testid="tonnage-standard-size">
                    {recSizeLabel}
                  </p>
                </div>
              </div>
            </div>

            {/* Breakdown */}
            <Card className="bg-white border border-gray-200 shadow-md">
              <CardContent className="p-6">
                <h3 className="text-gray-900 font-bold mb-4 text-sm uppercase tracking-wider">
                  Calculation Breakdown
                </h3>
                <div className="space-y-2 text-sm" data-testid="tonnage-breakdown">
                  <Row label={`Volume (${length} × ${breadth} × ${height})`} value={`${fmt(result.volume)} cu.ft`} />
                  <Row label="Base Load (Volume × 10)" value={`${fmt(result.baseBTU)} BTU`} />
                  <Row label={`Temperature Factor (${temp}°C)`} value={`× ${result.fTemp.toFixed(3)}`} />
                  <Row label="Subtotal after Temp" value={`${fmt(result.subtotal)} BTU`} />
                  {result.roofExtra > 0 && <Row label={`Roof Exposure (+${Math.round(ROOF_PCT[roof] * 100)}%)`} value={`+ ${fmt(result.roofExtra)} BTU`} highlight />}
                  {result.windowExtra > 0 && <Row label="Large Window Glass" value={`+ ${fmt(result.windowExtra)} BTU`} highlight />}
                  {result.occupantExtra > 0 && <Row label={`Extra Occupants (${occupants - 2})`} value={`+ ${fmt(result.occupantExtra)} BTU`} highlight />}
                  {result.equipmentExtra > 0 && <Row label={EQUIP_LABELS[equipment]} value={`+ ${fmt(result.equipmentExtra)} BTU`} highlight />}
                  <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-bold text-amber-600">
                    <span>Total Heat Load</span>
                    <span>{fmt(result.totalBTU)} BTU/hr</span>
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                  Your {fmt(result.volume)} cu.ft space needs ~{result.tonnage.toFixed(2)} tons of
                  cooling. Room volume sets the base load, the {temp}°C peak temperature adds a
                  {" "}×{result.fTemp.toFixed(3)} factor, and secondary heat gains push it to the
                  final recommendation above.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ===== LEAD CAPTURE: Download + WhatsApp ===== */}
        <div className="mt-14" data-testid="tc-lead-capture">
          <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 p-[2px] shadow-2xl shadow-amber-500/20">
            <div className="rounded-2xl bg-white p-6 md:p-10">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Copy */}
                <div>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
                    Get Your Free Report
                  </Badge>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    Download Your AC Tonnage Report/ HVAC Load &amp; Get a Quote on WhatsApp
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Enter your details to instantly download a AC Tonnage/ HVAC Load PDF with your full
                    calculation &amp; recommended tonnage. We&apos;ll open WhatsApp so you can send
                    your enquiry to MARCO AIR CONDITION and get the best price.
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      Professionally designed AC Tonnage Report/ HVAC Load
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      Full room details + recommended AC size
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      Instant WhatsApp enquiry to our team
                    </li>
                  </ul>
                </div>

                {/* Form */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                      <User className="h-4 w-4 text-amber-500" /> Your Name
                    </Label>
                    <Input
                      type="text"
                      value={name}
                      placeholder="e.g. Rahul Sharma"
                      onChange={(e) => {
                        setName(e.target.value);
                        setSent(false);
                        setError("");
                      }}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500"
                      data-testid="tc-lead-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                      <Smartphone className="h-4 w-4 text-amber-500" /> Mobile Number
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 text-sm bg-gray-100 border border-gray-300 rounded-md px-3 py-2">
                        +91
                      </span>
                      <Input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={mobile}
                        placeholder="10-digit mobile"
                        onChange={(e) => {
                          setMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                          setSent(false);
                          setError("");
                        }}
                        className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500"
                        data-testid="tc-lead-mobile"
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="text-red-500 text-xs" data-testid="tc-lead-error">
                      {error}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 disabled:opacity-70 text-black font-bold px-6 py-3 rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/30"
                    data-testid="tc-lead-submit"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Preparing your report...
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5" />
                        Download &amp; Send on WhatsApp
                      </>
                    )}
                  </button>

                  {sent && (
                    <div
                      className="flex items-start gap-2 text-green-700 text-xs bg-green-50 border border-green-300 rounded-lg p-3"
                      data-testid="tc-lead-success"
                    >
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        {refId && (
                          <span className="block font-semibold text-green-800 mb-1" data-testid="tc-lead-refid">
                            Reference ID: {refId}
                          </span>
                        )}
                        {pdfOk
                          ? "Your PDF has been downloaded and WhatsApp is opening with your enquiry."
                          : "WhatsApp is opening with your enquiry."}{" "}
                        If WhatsApp didn&apos;t open,{" "}
                        <a
                          href={getWhatsAppLink(
                            `Hi, I'm ${name || "a customer"} (+91 ${mobile}). Ref ID: ${refId}. I used the Tonnage Calculator and need a ${recSizeLabel} (${result.tonnage.toFixed(
                              2
                            )} tons). Please share the best price.`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline font-semibold text-green-800"
                        >
                          tap here
                        </a>
                        .
                      </span>
                    </div>
                  )}

                  <p className="text-gray-500 text-[11px] flex items-center gap-1">
                    <Send className="h-3 w-3" />
                    Your enquiry goes directly to MARCO AIR CONDITION (+91 98258 84460).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== AC SUGGESTIONS ===== */}
        <div className="mt-14">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Recommended ACs for Your Space
            </h3>
            <p className="text-gray-600 text-sm">
              {result.standard ? `${result.standard} Ton` : "High-capacity"} {result.systemType} options from our trusted brands
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-4">
            {SUGGESTED_BRANDS.map((brand, i) => (
              <a
                key={brand}
                href={getWhatsAppLink(
                  `Hi! Based on your Tonnage Calculator, I need a ${
                    result.standard ? result.standard + " Ton " : ""
                  }${result.systemType} (approx ${result.tonnage.toFixed(2)} tons / ${fmt(
                    result.totalBTU
                  )} BTU). Please share ${brand} models and price.`
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white hover:bg-amber-50 border border-gray-200 hover:border-amber-400 rounded-xl p-4 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-amber-500/20"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`tc-brand-suggestion-${i}`}
              >
                <Snowflake className="h-6 w-6 text-amber-500 mb-2 group-hover:rotate-90 transition-transform duration-500" />
                <span className="text-gray-900 font-semibold text-xs leading-tight mb-1">
                  {brand}
                </span>
                <span className="text-amber-600 text-[11px] font-bold">
                  {result.standard ? `${result.standard} Ton` : "VRF/Ductable"}
                </span>
                <span className="mt-2 text-[10px] text-green-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Enquire →
                </span>
              </a>
            ))}
          </div>
          <p className="text-center text-gray-600 text-xs mt-6 max-w-2xl mx-auto">
            * This is an estimate for guidance. Final sizing may vary with insulation, climate
            and usage. Contact MARCO AIR CONDITION for a precise site survey.
          </p>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className={highlight ? "text-amber-600 font-semibold" : "text-gray-900 font-medium"}>
      {value}
    </span>
  </div>
);

export default TonnageCalculatorSection;
